import { useCallback, useEffect, useRef, useState } from "react"
import { BsInstagram } from "react-icons/bs"
import Link from "next/link"
import validator from "validator"
import { animated, useIsomorphicLayoutEffect, useScroll } from "@react-spring/web"
import YouTube from "react-youtube"
import Hamburger from "hamburger-react"
import database from "../database.mjs"
import Footer from "../components/footer.js"
import SearchInput from "../components/search-input.js"
import { NextSeo } from "next-seo"
import UAParser from "ua-parser-js"
import Head from "next/head.js"
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion"

export async function getServerSideProps() {
  const properties = database
    .prepare("select id, address, area, price, beds, baths, audience from properties_to_let order by timestamp desc limit 16")
    .all()

  const all_properties_for_sale = database
    .prepare("select id, address, area, price from properties_for_sale order by timestamp desc limit 250")
    .all()

  const all_properties_to_let = database
    .prepare("select id, address, area, price from properties_to_let order by timestamp desc limit 250")
    .all()

  for (const property of properties) {
    const id = property?.["id"]
    const filename = database.prepare("select filename from properties_to_let_photos where property_id = ? limit 1").get(id)?.[
      "filename"
    ]

    property["thumbnail"] = `/api/images/${filename}.webp`
  }

  let posts = database.prepare("select * from instagram_grid limit 4").all() || []
  if (posts?.length < 4) {
    while (posts.length < 4) {
      posts = [...posts, null]
    }
  }

  return {
    props: {
      properties,
      all_properties_for_sale,
      all_properties_to_let,
      posts,
    },
  }
}

function HomePage({ properties, all_properties_for_sale, all_properties_to_let, posts }) {
  const [rotate1, setRotate1] = useState(false)
  const [rotate2, setRotate2] = useState(false)
  const [rotate3, setRotate3] = useState(false)
  const [rotate4, setRotate4] = useState(false)
  const [rotate5, setRotate5] = useState(false)
  const [rotate6, setRotate6] = useState(false)
  const [rotate7, setRotate7] = useState(false)
  const [rotate8, setRotate8] = useState(false)
  const [rotate9, setRotate9] = useState(false)
  const [rotate10, setRotate10] = useState(false)
  const [rotate11, setRotate11] = useState(false)
  const [rotate12, setRotate12] = useState(false)

  const [rotate99, setRotate99] = useState(false)
  const [rotate100, setRotate100] = useState(false)

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isMenuSearchOpen, setIsMenuSearchOpen] = useState(false)

  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState([])

  const searchInputRef = useRef()

  const [docTitle, setDocTitle] = useState()

  useEffect(() => {
    const parser = new UAParser(navigator?.userAgent)
    const device = parser.getDevice()
    if (device.vendor === "Apple" && (device.type === "mobile" || device.type === "tablet")) {
      setDocTitle("Huntleys")
    }

    const hamburgerElement = document.querySelector(".hamburger-react")

    if (hamburgerElement) {
      hamburgerElement.setAttribute("aria-label", "menu")
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    let timeout

    if (isSearchOpen) {
      searchInputRef.current?.focus()
      timeout = setTimeout(() => {
        window.scrollTo(0, 0)
      }, 100)
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [isSearchOpen])

  useEffect(() => {
    const timeout = setTimeout(() => {
      ;(async () => {
        const response = await fetch(`/api/properties${validator.isEmpty(searchQuery) ? "" : `?area=${searchQuery}`}`)

        if (response.ok) {
          const body = await response.json()

          setSearchResults(body)
        }
      })()
    }, 100)

    return () => clearTimeout(timeout)
  }, [searchQuery, isSearchOpen, isMenuSearchOpen])

  useEffect(() => {
    if (isSearchOpen || isMenuOpen) {
      document.body.classList.add("overflow-hidden")
    } else {
      document.body.classList.remove("overflow-hidden")
    }
  }, [isSearchOpen, isMenuOpen])

  const escFunction = useCallback(event => {
    if (event.key === "Escape") {
      setIsSearchOpen(false)
      setIsMenuSearchOpen(false)
      setIsMenuOpen(false)
      setIsVideoOpen(false)
    }
  }, [])

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false)

    return () => {
      document.removeEventListener("keydown", escFunction, false)
    }
  }, [escFunction])

  const { scrollY } = useScroll({ reverse: true })

  const [windowInnerHeight, setWindowInnerHeight] = useState(0)
  const [windowInnerWidth, setWindowInnerWidth] = useState(0)

  useIsomorphicLayoutEffect(() => {
    setWindowInnerHeight(window.innerHeight)
    setWindowInnerWidth(window.innerWidth)
    setHeight(ref?.current?.clientHeight)
    setGreenBoxHeight(greenBoxRef?.current?.clientHeight)
  })

  useEffect(() => {
    if (!windowInnerHeight) return

    const listener = () => {
      if (window.scrollY >= windowInnerHeight) {
        document?.getElementById("video")?.pause()
      } else {
        document?.getElementById("video")?.play()
      }
    }

    window.addEventListener("scroll", listener, { passive: true })

    return () => window.removeEventListener("scroll", listener)
  }, [windowInnerHeight])

  useEffect(() => {
    const onResize = () => {
      setWindowInnerHeight(window.innerHeight)
      setWindowInnerWidth(window.innerWidth)
      setHeight(ref?.current?.clientHeight)
      setGreenBoxHeight(greenBoxRef?.current?.clientHeight)
    }
    window.addEventListener("resize", onResize, { passive: true })

    return () => window.removeEventListener("resize", onResize)
  }, [])

  const [height, setHeight] = useState(0)
  const ref = useRef(null)
  const greenBoxRef = useRef(null)

  const [greenBoxHeight, setGreenBoxHeight] = useState(0)
  const [isVideoOpen, setIsVideoOpen] = useState(false)

  const [showVideoOverlay, setShowVideoOverlay] = useState(false)

  useEffect(() => {
    if (!isVideoOpen) {
      setShowVideoOverlay(false)

      return
    }

    const handle = setTimeout(() => setShowVideoOverlay(true), 300)

    return () => clearTimeout(handle)
  }, [isVideoOpen])

  const [carouselIndex, setCarouselIndex] = useState(0)
  const carouselRef = useRef(null)

  useEffect(() => {
    const listener = () => {
      setWindowInnerHeight(window?.innerHeight)
    }

    window.addEventListener("resize", listener)

    return () => window.removeEventListener("resize", listener)
  }, [])

  return (
    <>
      <NextSeo
        title={docTitle || "Huntleys Estate & Letting Agents in Loughborough"}
        description="Huntleys Estate Agents in Loughborough is a trusted and independent estate and letting agency. Discover a diverse portfolio of properties available for rent in the LE11 and LE12 areas. Contact us today to find your dream home or discuss your property needs."
        canonical="https://www.huntleys.net"
      />
      <Head>
        <meta property="og:image" content="https://www.huntleys.net/images/rebecca-og.jpg" />
      </Head>
      <div className="overflow-hidden max-w-screen">
        <div
          className="absolute max-h-screen overflow-scroll opacity-0 pointer-events-none top-12 left-12 right-12"
          style={{ fontSize: "2rem" }}
        >
          <h1>Estate Agents Loughborough</h1>
          <p className="mt-4">
            Loughborough, located in Leicestershire, is a market town situated in close proximity to Leicester, Nottingham, East
            Midlands Airport, and Derby. The town boasts a rich historical background, showcasing numerous sites and museums for
            visitors to explore. Additionally, its advantageous location provides easy access to outdoor attractions like the
            Bradgate Park and Beacon Hill. Notably, Loughborough is home to John Taylor Bellfounders, the largest bell foundry
            in the world. This renowned establishment has crafted bells for various notable locations, including St. Paul's
            Cathedral in London and York Minster. Moreover, Loughborough is recognized as the esteemed location of Loughborough
            University, which received the prestigious title of University of the Year at the 2020 WhatUni Student Choice
            Awards. At Huntleys, our experienced estate agents are dedicated to our local area. We have effective marketing
            strategies that generate interest in your property, whether you want to sell or rent it. With a strong presence on
            the high street, eye-catching signage, and advertisements on popular property websites like Rightmove and Zoopla, we
            give your property the best chance of attracting the right buyer or tenant. Our top priority is providing excellent
            customer service and bringing people and properties together.
          </p>
          {/* <p className="mt-2">PROPERTIES FOR SALE</p>
          <ul className="flex-col mt-4 gap-y-6">
            {all_properties_for_sale.map((property, index) => (
              <li key={index} style={{ marginTop: 16 }}>
                <a itemType="http://schema.org/Residence" href={`/rent/${property?.id}`}>
                  {`${property?.address}, ${property?.area} is Available For Sale ${new Intl.NumberFormat("en-GB", {
                    style: "currency",
                    currency: "GBP",
                    maximumFractionDigits: 0,
                  }).format(property?.price)}`.trim()}
                </a>
              </li>
            ))}
          </ul> */}
          <p className="mt-2">PROPERTIES TO LET</p>
          <ul className="flex-col mt-4 gap-y-6">
            {all_properties_to_let.map((property, index) => (
              <li key={index} style={{ marginTop: 16 }}>
                <a itemType="http://schema.org/Residence" href={`/rent/${property?.id}`}>
                  {`${property?.address}, ${property?.area} is Available To Let ${new Intl.NumberFormat("en-GB", {
                    style: "currency",
                    currency: "GBP",
                    maximumFractionDigits: 0,
                  }).format(property?.price)}`.trim()}
                </a>
              </li>
            ))}
          </ul>
          <div className="flex flex-col mt-6 gap-y-5">
            <div className="gap-y-4">
              <h2>What percentage do most estate agents charge?</h2>
              <p>
                The percentage that most estate agents charge for their services can vary depending on various factors,
                including the location, the value of the property, and the specific terms negotiated between the agent and the
                client. However, it is common for estate agents to charge a commission fee based on a percentage of the final
                sale price of the property.
              </p>
              <p>
                The typical range for estate agent commissions is between 1% and 3% of the sale price. The exact percentage can
                be influenced by market conditions, the level of competition among agents in a given area, and the services
                provided by the agent.
              </p>
              <p>
                It's important to note that commission structures can vary, and some agents may charge a fixed fee rather than a
                percentage. Additionally, there may be additional costs associated with marketing, advertising, or other
                services provided by the agent. It's always a good idea to discuss and negotiate the commission and any
                associated fees with your chosen estate agent before entering into a contract or agreement.
              </p>
            </div>
            <div className="gap-y-4">
              <h2>How do you find the right estate agent?</h2>
              <p>
                1. Research local agents: Start by researching estate agents in your local area. Look for agents with a strong
                presence and reputation in the market. You can do this by checking online listings, reading reviews and
                testimonials, asking for recommendations from friends or family, or even visiting local estate agent offices.
              </p>
              <p>
                2. Check credentials and experience: Verify the credentials and experience of the estate agents you are
                considering. Look for professional affiliations, licenses, and certifications that demonstrate their expertise
                and adherence to industry standards. Consider the number of years they have been in business and their track
                record of successful sales.
              </p>
              <p>
                3. Interview multiple agents: It's a good idea to interview multiple estate agents before making a decision. Set
                up meetings or phone calls with them to discuss your requirements, ask questions about their services, and gauge
                their knowledge and communication skills. This will help you assess their professionalism, responsiveness, and
                ability to understand and meet your needs.
              </p>
              <p>
                4. Request market analysis and valuation: Ask the shortlisted estate agents to provide you with a market
                analysis and property valuation. This will give you an idea of their expertise in your local market and their
                ability to accurately assess the value of your property. Compare the analyses provided by different agents to
                evaluate their knowledge and approach.
              </p>
              <p>
                5. Review marketing strategies: Inquire about the marketing strategies and channels that the estate agents
                utilize to promote properties. A strong marketing plan is crucial for maximizing exposure and attracting
                potential buyers. Ask about their online presence, use of professional photography, virtual tours, open houses,
                and other marketing techniques they employ.
              </p>
              <p>
                6. Consider fees and contracts: Discuss the commission or fee structure with the estate agents and carefully
                review any contracts or agreements they present. Understand the terms and conditions, including the duration of
                the contract, termination clauses, and any additional costs or fees associated with their services.
              </p>
              <p>
                7. Trust your instincts: Ultimately, trust your instincts and choose an estate agent with whom you feel
                comfortable and confident. Consider their professionalism, integrity, and compatibility with your specific
                needs. A good working relationship and effective communication are key to a successful partnership with your
                estate agent. Remember, finding the right estate agent is an important decision, as they play a significant role
                in the selling or buying process. Taking the time to research, interview, and compare different agents will help
                you make an informed choice and increase your chances of a successful transaction. Do Estate agents charge fees?
                Yes, estate agents typically charge fees for their services. These fees are commonly referred to as commission
                fees and are paid by the seller of the property. The commission is usually a percentage of the final sale price
                and is payable to the estate agent upon the successful completion of the sale.
              </p>
            </div>
            <div className="gap-y-4">
              <h2>Do Estate agents charge fees?</h2>
              <p>
                Yes, estate agents typically charge fees for their services. These fees are commonly referred to as commission
                fees and are paid by the seller of the property. The commission is usually a percentage of the final sale price
                and is payable to the estate agent upon the successful completion of the sale.
              </p>
              <p>
                The commission percentage can vary depending on factors such as the location, market conditions, and the
                specific terms negotiated between the agent and the client. In many countries, including the United States and
                the United Kingdom, the typical range for estate agent commissions is between 1% and 3% of the sale price.
                However, it's important to note that commission structures can differ, and some agents may charge a fixed fee
                instead of a percentage.
              </p>
              <p>
                When engaging the services of an estate agent, it is essential to have a clear understanding of their fees and
                any associated costs. Make sure to discuss and negotiate the commission structure and any additional fees, such
                as marketing expenses, upfront, and include them in the contract or agreement.
              </p>
            </div>
            <div className="gap-y-4">
              <h2>Is it best to use a local estate agent?</h2>
              <p>
                While using a local estate agent can be advantageous, it's important to consider the individual agent's
                reputation, experience, and track record alongside their local expertise. Conduct research, interview multiple
                agents, and choose one that not only has a strong local presence but also demonstrates professionalism,
                effective communication, and a good understanding of your specific requirements.
              </p>
              <p>
                However, it's worth noting that in certain situations, such as when dealing with specialized or unique
                properties, you may need to consider agents from outside the local area who have expertise in those specific
                property types or markets. Ultimately, the choice of whether to use a local estate agent depends on your
                specific needs and circumstances.
              </p>
            </div>
            <div className="gap-y-4">
              <h2>Why do people still use estate agents?</h2>
              <p>
                While the real estate landscape is evolving, many people still choose to work with estate agents due to the
                expertise, resources, and convenience they offer. By leveraging their knowledge and services, individuals can
                benefit from professional assistance and increase their chances of a successful and smooth real estate
                transaction.
              </p>
            </div>
            <div className="gap-y-4">
              <h2>Why do estate agents ask for national insurance number?</h2>
              <p>
                In some countries, such as the United Kingdom, estate agents may ask for a national insurance number as part of
                their due diligence and regulatory requirements. Here are a few reasons why estate agents may request a national
                insurance number:
              </p>
              <p>
                1. Identity Verification: Requesting a national insurance number helps estate agents verify the identity of
                their clients. This is important for compliance with anti-money laundering regulations and to prevent fraudulent
                activity. By confirming the national insurance number, estate agents can ensure that they are dealing with the
                correct individual and maintain the integrity of the transaction.
              </p>
              <p>
                2. Financial Checks: Estate agents may use national insurance numbers to conduct financial checks or obtain
                credit references on clients. These checks help agents assess the financial stability and suitability of buyers
                or tenants. It enables them to gauge whether the client has the financial means to proceed with a purchase or
                rental agreement.
              </p>
              <p>
                3. Government Reporting: In some cases, estate agents are required to report certain transactions to government
                authorities or regulatory bodies. Providing the national insurance number allows agents to fulfill their
                reporting obligations accurately. This helps ensure compliance with tax regulations, track property
                transactions, and prevent money laundering or other illegal activities.
              </p>
              <p>
                It's important to note that the specific reasons for requesting a national insurance number may vary depending
                on local regulations and industry practices. Estate agents have a legal and ethical responsibility to protect
                the interests of their clients and uphold relevant laws and regulations.
              </p>
              <p>
                When providing personal information such as a national insurance number, it is crucial to ensure that you are
                dealing with a reputable and trustworthy estate agent. Always verify the legitimacy of the agent or agency and
                understand how they will handle and protect your personal information in accordance with data protection laws
                and regulations.
              </p>
            </div>
          </div>
        </div>
        <div style={{ height: windowInnerHeight * 1.5 + height }}></div>
        <div className="fixed top-0 bottom-0 left-0 right-0 w-full h-screen">
          <img
            alt="huntleys estate agents in loughborough - mobile tour and walkthrough of house"
            className="object-cover w-full h-full sm:hidden"
            src="/cover.jpg"
          />
          <img
            alt="huntleys estate agents in loughborough - tour and walkthrough of house in living room"
            className="hidden object-cover w-full h-full sm:block"
            src="/images/desktop-cover.webp"
          />
        </div>
        <div className="fixed top-0 bottom-0 left-0 right-0 w-full h-screen overflow-hidden">
          <div className="w-full h-full bg-stone-900">
            <video
              preload="none"
              poster="/cover.jpg"
              autoPlay
              className="block object-cover w-full h-full sm:hidden"
              loop
              muted
              playsInline
              src="/promo-3.mp4"
            />
            <video
              preload="none"
              autoPlay
              className="hidden object-cover w-full h-full sm:block"
              loop
              muted
              playsInline
              src="/house-tour.mp4"
              poster="/images/desktop-cover.webp"
            />
          </div>
          <div className="absolute top-0 bottom-0 left-0 right-0 h-screen bg-gradient-to-t from-stone-900 via-transparent to-stone-900" />
          <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center px-4">
            <div className="flex flex-col items-center w-full gap-y-6">
              <h2 className="font-serif text-[2.75rem] leading-none font-semibold text-center text-white sm:text-6xl xs:text-3xl ">
                Start your journey
              </h2>
              <div className="pointer-events-none opacity-0 flex justify-center w-full mx-auto sm:mt-[2rem] font-medium gap-x-4 sm:gap-x-8 drop-shadow-xl max-w-[512px]">
                <Link
                  className="flex-1 text-center w-full h-[57px] hover:text-stone-900 text-stone-900 transition-all duration-300 rounded select-none bg-cpink border-cpink border-2 hover:bg-white hover:border-white flex items-center justify-center"
                  href="/rent"
                >
                  Start your journey
                </Link>
              </div>
            </div>
          </div>
          {isSearchOpen && (
            <div className="absolute hidden sm:block top-4 left-4 right-4">
              <div
                className={[
                  `flex items-center relative mx-auto sm:max-w-[calc(640px+128px)] md:max-w-[calc(768px+128px)] lg:max-w-[calc(1024px+128px)] xl:max-w-[calc(1280px+128px)] justify-between p-2 rounded 2xl:max-w-[calc(1536px+128px)] w-full transition-all duration-300 bg-teal-900`,
                  isMenuOpen ? "sm:shadow-sm" : "mt-auto",
                  isSearchOpen ? "hidden sm:flex" : "",
                ].join(" ")}
              >
                <Link href="/" className="ml-2 pointer-events-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 230 52" className="h-6">
                    <path
                      fill="#fff"
                      d="M26.235 34.235V23.529H13.176v10.706l3.883 3.53v1.294H0v-1.295l3.882-3.529V8.588L0 5.058V3.766h17.059V5.06l-3.883 3.53v11.176h13.06V8.588l-3.883-3.53V3.766H39.41V5.06l-3.882 3.53v25.646l3.882 3.53v1.294H22.235v-1.295l4-3.529zM44.823 18.47l-3.176-2.352v-1.294L53.41 12v15.53c0 1.882.353 3.293.941 4.235.589.823 1.647 1.294 3.059 1.294 1.176 0 2.588-.353 4.118-.941V18.47l-3.177-2.353v-1.294L70.117 12v22.117l4.235 1.648v1.294l-9.294 2.706-3.53-4.706c-.94 1.411-2 2.47-3.411 3.294-1.412.823-2.941 1.176-4.588 1.176-2.706 0-4.824-.823-6.353-2.47-1.647-1.647-2.47-3.883-2.47-6.706V18.47h.117zm52.235 16.706V24c0-3.647-1.294-5.53-3.882-5.53-1.647 0-3.177.471-4.706 1.412v15.294l2.942 2.588v1.295H76.588v-1.294l3.176-2.589v-16.47l-3.411-2.47V14.94L86 11.882l1.647 4.824c1.411-1.53 2.941-2.706 4.47-3.53 1.53-.823 3.177-1.176 5.06-1.176 2.587 0 4.705.823 6.234 2.47 1.529 1.648 2.353 3.883 2.353 6.706v14.118L108.94 38v1.294H94.117V38l2.941-2.824zm26.118-29.411v6.588h7.058v3.764h-7.058v14c0 1.883.353 3.177.941 3.883s1.647 1.059 2.941 1.059c1.412 0 2.824-.118 3.882-.471v1.294c-.47.47-1.176 1.059-2.235 1.647-.941.588-2.117 1.177-3.412 1.53-1.293.352-2.588.588-3.764.588-2.236 0-4-.588-5.294-2-1.294-1.294-1.883-3.177-1.883-5.53v-16h-4.118v-2.47l4.118-.588V7.765l8.824-2zm13.175 29.411V6.471l-3.176-2.353V2.824L144.94 0v35.176l3.176 2.588v1.294h-14.941v-1.294l3.176-2.588zm39.765-9.882h-15.647c.118 2 .471 3.647.942 4.941.471 1.412 1.412 2.47 2.588 3.294 1.177.824 2.941 1.294 5.177 1.294 1.764 0 3.764-.353 5.882-1.176l.823 1.647c-1.176 1.294-2.706 2.235-4.588 3.059-1.882.823-3.882 1.176-6.235 1.176-4.353 0-7.765-1.176-10.236-3.53-2.47-2.352-3.646-5.646-3.646-9.882 0-1.882.352-3.647.941-5.411a11.568 11.568 0 012.823-4.588c1.177-1.294 2.706-2.353 4.471-3.177 1.765-.823 3.647-1.176 5.647-1.176 1.294 0 2.588.117 3.882.47 1.295.353 2.471.941 3.647 1.883 1.059.823 2 2 2.706 3.411.706 1.412 1.059 3.06 1.059 5.059.117.941 0 1.765-.236 2.706zM164.822 16c-.588 0-1.176.235-1.882.588-.706.47-1.176 1.177-1.647 2.235-.47 1.06-.706 2.353-.824 3.765l8.235-1.059c-.116-1.882-.588-3.294-1.176-4.235-.588-.823-1.529-1.294-2.706-1.294zm17.177 36l-2.352-7.647c.235 0 1.059-.118 2.588-.236 1.529-.117 2.823-.352 3.764-.705 1.059-.353 2.001-.824 2.824-1.647.824-.706 1.647-1.648 2.235-2.706l-10.47-22.706-3.176-2.588V12.47h15.647v1.294l-3.177 2.588 5.294 12.941 4.823-12.941-3.176-2.588V12.47h10.823v1.294l-3.411 2.588-9.412 22.823c-1.53 3.648-2.706 6.236-3.647 7.647-.941 1.412-2.117 2.589-3.53 3.412-1.411.824-3.294 1.412-5.647 1.765zm47.882-21.059c0 2.824-1.059 4.941-3.176 6.47-2.118 1.412-5.059 2.236-8.824 2.236-1.294 0-2.588-.118-3.882-.236-1.294-.117-2.941-.588-4.706-1.058v-8.824h1.295l4.117 6.118c.824.235 1.646.47 2.235.47 3.059 0 4.706-.94 4.706-2.705 0-.471-.236-1.06-.706-1.647-.47-.589-1.765-1.412-3.882-2.353-2.236-.941-3.765-1.883-4.706-2.706-.941-.824-1.648-1.647-2.118-2.706-.471-1.059-.706-2.235-.706-3.53 0-2.47.942-4.588 2.941-6.117 2.001-1.53 4.707-2.353 8.236-2.353 2.588 0 4.941.47 7.293 1.53v7.764h-1.293l-3.882-5.53c-2.001 0-3.294.236-3.883.707-.706.47-.941 1.176-.941 2 0 .352.117.705.235 1.058.235.353.471.706.941 1.06.471.352 1.883 1.058 4.353 2.235 2.471 1.176 4.236 2.352 5.059 3.646.824 1.295 1.294 2.706 1.294 4.471z"
                    ></path>
                  </svg>
                </Link>
                <button
                  className="rounded pointer-events-auto hover:bg-teal-800 sm:hidden"
                  onClick={() => {
                    setIsMenuSearchOpen(false)
                    setIsMenuOpen(isMenuOpen => !isMenuOpen)
                  }}
                >
                  <Hamburger toggled={isMenuOpen} color="#fff" direction="sm" size={18} />
                </button>
                <div className="hidden text-white sm:flex gap-x-4">
                  {/* <Link
                    href="/rent"
                    className="hidden px-3 py-2 transition-all duration-300 rounded pointer-events-auto text-cente hover:bg-teal-800 sm:block"
                  >
                    Buy
                  </Link> */}
                  <Link
                    href="/rent"
                    className="hidden px-3 py-2 transition-all duration-300 rounded pointer-events-auto text-cente hover:bg-teal-800 sm:block"
                  >
                    Properties
                  </Link>
                  <a
                    className="hidden px-3 py-2 transition-all duration-300 rounded pointer-events-auto text-cente hover:bg-teal-800 sm:block"
                    href="#"
                  >
                    Landlords
                  </a>
                  <Link
                    href="/valuation?type=let"
                    className="hidden px-3 py-2 transition-all duration-300 rounded pointer-events-auto text-cente hover:bg-teal-800 sm:block"
                  >
                    Sell
                  </Link>
                  <Link
                    href="/contact"
                    className="hidden px-3 py-2 transition-all duration-300 rounded pointer-events-auto text-cente hover:bg-teal-800 sm:block"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          )}
          <div
            className={[
              "fixed top-0 bottom-0 left-0 right-0 bg-transparent transition-all duration-300 backdrop-blur",
              isSearchOpen ? "opacity-100 visible cursor-pointer pointer-events-auto" : "opacity-0 pointer-events-none",
            ].join(" ")}
            onClick={() => {
              setIsSearchOpen(false)
            }}
          />
          <div className="absolute flex items-center justify-center pointer-events-none bottom-4 top-4 left-4 right-4">
            <div className="flex items-center justify-center w-full h-full sm:max-h-[62.5vh] max-w-[512px]">
              <div
                className={[
                  `flex flex-col items-start w-full justify-start text-left rounded shadow text-stone-900 transition-all duration-300 ease-out sm:max-w-lg overflow-y-scroll no-scrollbar pointer-events-auto overflow-hidden`,
                  isSearchOpen
                    ? "min-h-full mt-0 z-50 border-stone-900 bg-cpink"
                    : "min-h-0 mt-[4.375rem] sm:mt-[7.3rem] border-white",
                ].join(" ")}
                onClick={() => {
                  window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
                  setIsSearchOpen(true)
                }}
              >
                <SearchInput
                  hasBorder
                  setIsSearchOpen={setIsSearchOpen}
                  isSearchOpen={isSearchOpen}
                  searchQuery={searchQuery}
                  searchInputRef={searchInputRef}
                  setSearchQuery={setSearchQuery}
                />
                <div className="w-full transition-all duration-300 max-h-0">
                  {searchResults?.areas?.length !== 0 && (
                    <div className="px-4 py-3 text-sm text-white bg-teal-900 border-b border-stone-900">
                      {validator.isEmpty(searchQuery) ? "Popular Areas" : "Areas"}
                    </div>
                  )}
                  {searchResults?.areas
                    ?.filter(result => result?.type !== "for_sale")
                    ?.map((result, index) => (
                      <Link
                        className={["pointer-events-auto transition-all duration-300"].join(" ")}
                        href={`/${result.type === "for_sale" ? "buy" : "rent"}?area=${result.area}`}
                        key={index}
                        prefetch={false}
                        onClick={() => document?.body?.classList.remove("overflow-hidden")}
                      >
                        <div
                          className={`flex items-center justify-between p-4 bg-transparent text-stone-800 hover:bg-cpink-50 ${
                            index === searchResults?.areas?.length - 1
                              ? "border-b border-stone-900"
                              : "border-b border-stone-900"
                          }`}
                        >
                          <div>
                            <p className="text-sm">{result.type === "for_sale" ? "Buy in" : "Rent in"}</p>
                            <p className="font-medium leading-tight">{result.area}</p>
                          </div>
                          <p className="text-right">{`${result.count} ${result.count > 1 ? "properties" : "property"}`}</p>
                        </div>
                      </Link>
                    ))}
                  {!validator.isEmpty(searchQuery) && (
                    <div className="px-4 py-3 text-sm text-white bg-teal-900 border-b border-stone-900">Properties</div>
                  )}
                  {searchResults?.properties
                    ?.filter(result => result?.type !== "for_sale")
                    ?.map((result, index) => (
                      <Link
                        className={["pointer-events-auto transition-all duration-300"].join(" ")}
                        href={`/${result.type === "for_sale" ? "buy" : "rent"}/${result?.id}`}
                        key={index}
                        prefetch={false}
                      >
                        <div className="flex items-center justify-between p-4 border-b border-stone-900 text-stone-800 hover:bg-cpink-50">
                          <div>
                            <p className="text-sm text-stone-900">{result.area}</p>
                            <p className="font-medium leading-tight">{result.address}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-md">
                              {new Intl.NumberFormat("en-GB", {
                                style: "currency",
                                currency: "GBP",
                                maximumFractionDigits: 0,
                              }).format(result?.price)}
                            </p>
                            <p className="text-sm text-stone-900">
                              {result?.beds} {result?.beds === 1 ? "bed" : "beds"} • {result.baths}{" "}
                              {result?.baths === 1 ? "bath" : "baths"}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="fixed top-0 left-0 right-0 overflow-hidden">
          <animated.div
            className={`fixed top-0 left-0 right-0 min-w-screen max-w-screen h-screen no-scrollbar bg-white`}
            style={{
              clipPath: scrollY.to(x => `circle(${x}px at ${windowInnerWidth / 2}px ${windowInnerHeight}px`),
              transform: scrollY.to(x => {
                if (x < windowInnerHeight * 1.5) return `translateY(0)`

                return `translateY(${windowInnerHeight * 1.5 - x}px)`
              }),
            }}
          >
            <div ref={ref}>
              <div className="h-[100vh] bg-stone-200 overflow-hidden">
                <div className="relative w-full h-full overflow-hidden bg-stone-900">
                  <animated.img
                    alt="Video"
                    className="object-cover w-full h-full select-none"
                    src="/images/hero.jpg"
                    style={{
                      transform: scrollY.to(x => {
                        return `scale(${1 + x / (windowInnerHeight * 8.75)})`
                      }),
                    }}
                  />
                  <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center bg-opacity-25 bg-gradient-to-b from-stone-900 via-transparent to-stone-900"></div>
                </div>
                <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center p-4">
                  <div className="z-10 flex flex-col items-center justify-center w-full mx-auto gap-y-6">
                    {/* <h2 className="z-30 mx-4 mt-[0.95rem] font-serif text-3xl font-semibold text-center text-white sm:text-5xl drop-shadow-xl">
                    Discover your home's value
                  </h2> */}
                    <div className="">
                      {/* <h2 className="font-serif text-[2.75rem] leading-none font-semibold text-center text-white sm:text-6xl xs:text-3xl -mt-8 sm:-mt-4 md:-mt-6">
                        Discover your home's value
                      </h2> */}
                      <h2 className="font-serif text-[2.75rem] leading-none font-semibold text-center text-white sm:text-6xl xs:text-3xl ">
                        Find out more
                      </h2>
                      {/* <h2 className="hidden lg:block font-serif text-[2.75rem] leading-none font-semibold text-center text-white sm:text-6xl xs:text-3xl ">
                        Discover your home's value
                      </h2> */}
                    </div>
                    <div className="flex justify-center w-full mx-auto sm:mt-[2rem] font-medium gap-x-4 sm:gap-x-8 drop-shadow-xl max-w-[512px]">
                      <Link
                        className="flex-1 text-center w-full h-[57px] hover:text-stone-900 text-stone-900 transition-all duration-300 rounded select-none bg-cpink border-cpink border-2 hover:bg-white hover:border-white flex items-center justify-center"
                        href="/valuation?type=let"
                      >
                        Arrange a valuation
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white">
                <div className="flex px-4 py-10 overflow-hidden bg-white">
                  <div className="w-full mx-auto overflow-hidden sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                    <div
                      className={[
                        `relative flex w-full mx-auto bg-stone-200 rounded overflow-hidden aspect-video max-h-[42rem]`,
                      ].join(" ")}
                    >
                      <div className="absolute top-0 left-0 right-0 h-24 opacity-75 bg-gradient-to-b from-stone-900 to-transparent"></div>
                      <p className="absolute text-white drop-shadow-md top-8 left-6 lg:top-12 lg:left-16">Let with Huntleys</p>
                      <div className="absolute bottom-0 left-0 right-0 h-32 opacity-75 bg-gradient-to-t from-stone-900 to-transparent"></div>
                      <div className="absolute left-8 bottom-6 lg:bottom-12 lg:left-16">
                        <h2 className="font-serif text-2xl font-medium text-white sm:text-3xl drop-shadow lg:text-4xl">
                          Kyfer Leech
                        </h2>
                        <p className="text-white drop-shadow-md">Lettings Valuer</p>
                      </div>
                      <div className="absolute top-0 bottom-0 left-0 right-0 flex">
                        <button
                          aria-label="Open Video"
                          className={[
                            `flex p-4 m-auto text-white transition-all duration-300 border-2 border-white rounded-full hover:bg-white hover:text-cpink md:p-6 drop-shadow md:border-4`,
                          ].join(" ")}
                          onClick={() => {
                            document.body.classList.add("overflow-hidden")
                            setIsVideoOpen(true)
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-5 h-5 m-auto md:h-6 md:w-6"
                          >
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </button>
                      </div>
                      <img
                        alt="Lettings Thumbnail"
                        className="object-cover w-full h-full select-none"
                        src="/images/let-thumbnail.webp"
                      />
                    </div>
                    <div
                      className={[
                        `fixed left-0 z-50 px-4 right-0`,
                        isVideoOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
                      ].join(" ")}
                      style={{ top: `calc(2.5rem + ${windowInnerHeight}px)` }}
                    >
                      <div
                        className={[
                          `w-full m-auto flex rounded bg-stone-900 max-h-[42rem] sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl aspect-video transition-all duration-300 overflow-hidden`,
                          isVideoOpen ? "sm:scale-[2] sm:duration-500 scale-[10]" : "scale-100",
                        ].join(" ")}
                      >
                        <img
                          alt="Sales Thumbnail"
                          className={[
                            `object-cover w-full h-full transition-all duration-300`,
                            isVideoOpen ? "opacity-0" : "opacity-100",
                          ].join(" ")}
                          src="/images/let-thumbnail.webp"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div id="faqs" className="flex items-center justify-center h-full px-4 pb-12 mt-6 bg-white text-stone-900">
                  <div className="flex flex-col items-center justify-center w-full lg:flex-row sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl gap-x-8">
                    <Accordion
                      className="flex flex-col items-start justify-center flex-1 w-full lg:flex-row gap-x-8"
                      allowZeroExpanded
                    >
                      <div className="flex flex-col justify-center flex-1 w-full h-full">
                        <AccordionItem className="w-full h-full">
                          <AccordionItemHeading
                            onClick={() => {
                              setRotate1(rotate1 => !rotate1)

                              setRotate2(false)
                              setRotate3(false)
                              setRotate4(false)
                              setRotate5(false)
                              setRotate6(false)
                              setRotate7(false)
                              setRotate8(false)
                              setRotate9(false)
                              setRotate10(false)
                              setRotate11(false)
                              setRotate12(false)
                              setRotate99(false)
                              setRotate100(false)
                            }}
                          >
                            <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                              <div className="mt-1">
                                <div className={`${rotate1 ? "rotate-45" : "rotate-0"} transition-all duration-300`}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-5 h-5"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                  </svg>
                                </div>
                              </div>
                              <p>Why choose Huntleys?</p>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel className="py-6">
                            <p>
                              Design and marketing are at the heart of our approach. With most people now looking for property
                              online, we can get our clients' properties in front of as many people as the most prominent
                              national agents. Where we differ is in the quality of our work. We create stunning digital
                              particulars highlighting the individual attributes of every property we market, with video tours
                              and floorplans as standard. As a result, Huntleys has had the highest market share of Agreed Lets
                              in LE11 and LE12 for the past four years running, while our planned West Bridgford office will
                              cover all of Rushcliffe.
                            </p>
                          </AccordionItemPanel>
                        </AccordionItem>
                        <AccordionItem>
                          <AccordionItemHeading
                            onClick={() => {
                              setRotate1(false)

                              setRotate2(rotate2 => !rotate2)

                              setRotate3(false)
                              setRotate4(false)
                              setRotate5(false)
                              setRotate6(false)
                              setRotate7(false)
                              setRotate8(false)
                              setRotate9(false)
                              setRotate10(false)
                              setRotate11(false)
                              setRotate12(false)
                              setRotate99(false)
                              setRotate100(false)
                            }}
                          >
                            <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                              <div className="mt-1">
                                <div className={`transition-all duration-300 ${rotate2 ? "rotate-45" : "rotate-0"}`}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-5 h-5"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                  </svg>
                                </div>
                              </div>
                              <p>Where is your office and what are your opening hours?</p>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel className="py-6">
                            <p>
                              Our office hours are Monday to Friday, 9:00am to 6:00pm, and Saturdays, 9:00am to 4:00pm. You can
                              find us conveniently located in the heart of Loughborough, right across from Bonkers and between
                              McDonald's and Marks & Spencer. If you're driving, the nearest parking is available at NCP The
                              Rushes. Our address is 7 Biggin Street, Loughborough, LE11 1UA.
                            </p>
                          </AccordionItemPanel>
                        </AccordionItem>
                      </div>
                      <div className="flex flex-col justify-center flex-1 w-full h-full">
                        <AccordionItem className="w-full h-full">
                          <AccordionItemHeading
                            onClick={() => {
                              setRotate7(rotate7 => !rotate7)

                              setRotate1(false)
                              setRotate2(false)
                              setRotate3(false)
                              setRotate4(false)
                              setRotate5(false)
                              setRotate6(false)

                              setRotate8(false)
                              setRotate9(false)
                              setRotate10(false)
                              setRotate11(false)
                              setRotate12(false)
                              setRotate99(false)
                              setRotate100(false)
                            }}
                          >
                            <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                              <div className="mt-1">
                                <div className={`transition-all duration-300 ${rotate7 ? "rotate-45" : "rotate-0"}`}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-5 h-5"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                  </svg>
                                </div>
                              </div>
                              <p>What areas do you cover?</p>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel className="py-6">
                            <p>
                              Our letting agency is located in Loughborough and serves Charnwood and Rushcliffe. We have a
                              strong presence in popular locations such as Quorn, Mountsorrel, Shepshed, Sileby, Leicester,
                              Barrow Upon Soar, Rothley, and Coalville. Our planned West Bridgford office will cover Clifton,
                              Cotgrave, and Ruddington.
                            </p>
                          </AccordionItemPanel>
                        </AccordionItem>
                        <div className="flex flex-col justify-center flex-1 w-full h-full">
                          <AccordionItem className="w-full h-full">
                            <AccordionItemHeading
                              onClick={() => {
                                setRotate99(rotate99 => !rotate99)

                                setRotate1(false)
                                setRotate2(false)
                                setRotate3(false)
                                setRotate4(false)
                                setRotate5(false)
                                setRotate6(false)
                                setRotate7(false)
                                setRotate8(false)
                                setRotate9(false)
                                setRotate10(false)
                                setRotate11(false)
                                setRotate12(false)
                                setRotate100(false)
                              }}
                            >
                              <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                                <div className="mt-1">
                                  <div className={`transition-all duration-300 ${rotate99 ? "rotate-45" : "rotate-0"}`}>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      strokeWidth={1.5}
                                      stroke="currentColor"
                                      className="w-5 h-5"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                  </div>
                                </div>
                                <p>What is happening to house prices in 2023?</p>
                              </AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel className="py-6">
                              <p>
                                High inflation has forced interest rates to rise, slowing the housing market and reducing the
                                number of transactions. This will, in turn, affect what prices houses are sold for, as reduced
                                demand means buyers will be in a better position to negotiate. So far, the slowdown has been
                                modest, buffered by continued demand for larger homes, but it could continue if interest rates
                                are to rise again.
                              </p>
                              <p className="mt-4">
                                We expected a modest asking price decline in 2023 of around 5%. This would mean the cost of the
                                average property returning to April 2022 prices, which is still significantly above pre-pandemic
                                levels. Prices are then likely to remain flat in 2024 before rising again throughout 2025.
                              </p>
                              <p className="mt-4">
                                Want to know how this affects you?{" "}
                                <Link className="underline text-cpink-200" href="/valuation?type=let" prefetch={false}>
                                  Book a valuation
                                </Link>{" "}
                                to learn more.
                              </p>
                            </AccordionItemPanel>
                          </AccordionItem>
                          {/* <AccordionItem>
                          <AccordionItemHeading
                            onClick={() => {
                              setRotate100(rotate100 => !rotate100)

                              setRotate1(false)
                              setRotate2(false)
                              setRotate3(false)
                              setRotate4(false)
                              setRotate5(false)
                              setRotate6(false)
                              setRotate7(false)
                              setRotate8(false)
                              setRotate9(false)
                              setRotate10(false)
                              setRotate11(false)
                              setRotate12(false)
                              setRotate99(false)
                            }}
                          >
                            <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                              <div className="mt-1">
                                <div className={`transition-all duration-300 ${rotate100 ? "rotate-45" : "rotate-0"}`}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-5 h-5"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                  </svg>
                                </div>
                              </div>
                              <p>What are active and passive buyers?</p>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel className="py-6">
                            <p>
                              Active buyers are what Rightmove, Zoopla and the other property portals cater to very well.
                              They're buyers who know what they're looking for; they're on the internet searching for properties
                              and will have new listings notifications set up. Passive buyers are those who are not actively
                              looking for a new home but are open to moving if the right property comes along.
                            </p>
                            <p className="mt-4">
                              The best way to reach these passive buyers is through social media platforms such as Facebook,
                              Instagram, TikTok, etc. With Charnwood's largest social media following of any estate agent, we
                              reach hundreds of thousands of these potential buyers monthly. More potential buyers mean more
                              interest, more viewings and ultimately, a higher price for your home.
                            </p>
                            <ul className="mt-4 ml-4">
                              <li className="list-disc">
                                Four out of five of our posts about homes for sale led to an enquiry.
                              </li>
                              <li className="mt-3 list-disc">A third of viewing requests come through WhatsApp.</li>
                            </ul>
                          </AccordionItemPanel>
                        </AccordionItem> */}
                        </div>
                        {/* <AccordionItem>
                          <AccordionItemHeading
                            onClick={() => {
                              setRotate1(false)
                              setRotate2(false)
                              setRotate3(false)
                              setRotate4(false)
                              setRotate5(false)
                              setRotate6(false)
                              setRotate7(false)

                              setRotate8(rotate8 => !rotate8)

                              setRotate9(false)
                              setRotate10(false)
                              setRotate11(false)
                              setRotate12(false)
                              setRotate99(false)
                              setRotate100(false)
                            }}
                          >
                            <AccordionItemButton className="flex py-3 text-lg font-medium border-b border-stone-900 gap-x-2">
                              <div className="mt-1">
                                <div className={`transition-all duration-300 ${rotate8 ? "rotate-45" : "rotate-0"}`}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-5 h-5"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                  </svg>
                                </div>
                              </div>
                              <p>Do you offer mortgages?</p>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel className="py-6">
                            <p>
                              While we do not offer mortgage advice directly, we have a strong partnership with the{" "}
                              <Link
                                className="underline text-cpink-200"
                                href="https://www.mortgageadvicebureau.com/mortgage-advisers/quorn/huntleys-estate-agents"
                                target="_blank"
                              >
                                Mortgage Advice Bureau
                              </Link>
                              , a trusted provider of independent mortgage advice. Finding a mortgage has never been easier.
                              Visit their website for detailed information and access to a variety of helpful calculators that
                              can estimate how much you can borrow.
                            </p>
                          </AccordionItemPanel>
                        </AccordionItem> */}
                      </div>
                    </Accordion>
                  </div>
                </div>
                <div className="relative py-6 mt-12 sm:py-12 bg-cpink">
                  <h2 className="z-30 font-serif text-3xl font-bold text-center text-stone-900 sm:text-5xl drop-shadow">
                    Instagram worthy homes
                  </h2>
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center bg-white top-96 sm:top-[36rem] md:top-56 lg:top-96"></div>
                  <div>
                    <div className="relative flex justify-center mt-8 sm:mt-12">
                      <div className="w-full px-4">
                        <div className="grid justify-center max-w-lg grid-cols-2 gap-6 mx-auto overflow-hidden transition-all duration-300 md:grid-cols-4 snap-x snap-mandatory sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl no-scrollbar">
                          {posts.map((item, index) => {
                            return (
                              <div
                                key={index}
                                className="relative flex-1 overflow-hidden rounded select-none aspect-square drop-shadow-sm"
                              >
                                <BsInstagram className="absolute z-50 w-5 h-5 text-white scale-100 shadow-sm sm:w-6 sm:h-6 top-4 right-4" />
                                <Link
                                  href={
                                    typeof item?.link === "string"
                                      ? item?.link !== ""
                                        ? item?.link
                                        : "https://instagram.com/huntleysestateagents"
                                      : "https://instagram.com/huntleysestateagents"
                                  }
                                  target="_blank"
                                  className={`bg-stone-300 w-full h-full`}
                                >
                                  <img
                                    alt={item?.alt || "instagram photo"}
                                    className="object-cover w-full h-full transition-all duration-500 hover:scale-105"
                                    src={
                                      typeof item?.image === "string"
                                        ? item?.image !== ""
                                          ? `/api/images/${item?.image}.webp`
                                          : `/images/${index + 1}.jpg`
                                        : `/images/${index + 1}.jpg`
                                    }
                                  />
                                </Link>
                              </div>
                            )
                          })}
                          {/* {[...Array(4)].map((item, index) => {
                            return (
                              <div
                                key={index}
                                className="relative flex-1 overflow-hidden rounded select-none aspect-square drop-shadow-sm"
                              >
                                <BsInstagram className="absolute z-50 w-5 h-5 text-white scale-100 shadow-sm sm:w-6 sm:h-6 top-4 right-4" />
                                <Link
                                  href="https://instagram.com/huntleysestateagents"
                                  target="_blank"
                                  className={`bg-stone-300 w-full h-full`}
                                >
                                  <img
                                    alt="Instagram Post"
                                    className="object-cover w-full h-full transition-all duration-500 hover:scale-105"
                                    src={`/images/${index + 1}.jpg`}
                                  />
                                </Link>
                              </div>
                            )
                          })} */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="py-10 bg-white">
                  <h2
                    className="z-30 mx-4 font-serif text-3xl font-bold text-center text-stone-900 sm:text-5xl drop-shadow"
                    style={{ lineHeight: 1.125 }}
                  >
                    This week at Huntleys
                  </h2>
                  <div className="relative px-4 mt-6">
                    <div
                      className="flex mx-auto overflow-x-scroll transition-all duration-300 snap-x snap-mandatory lg:gap-x-6 gap-x-4 sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl no-scrollbar"
                      ref={carouselRef}
                    >
                      {properties.map((property, index) => {
                        return (
                          <Link
                            prefetch={false}
                            key={index}
                            className="snap-start relative block min-w-full overflow-hidden transition-all duration-300 rounded md:w-1/2 md:min-w-[calc(50%-0.5rem)] bg-stone-400 lg:max-w-1/3 lg:min-w-[calc(33.33%-1rem)] xl:max-w-1/4 xl:min-w-[calc(25%-1.1rem)] scroll-smooth shadow-sm"
                            href={`/rent/${property?.id}`}
                          >
                            <div className="relative top-0 bottom-0 left-0 right-0 w-full h-full">
                              <div className="relative aspect-[1.625] w-full overflow-hidden">
                                <img
                                  alt={`${property?.address}, ${property?.area?.toUpperCase()}`}
                                  itemType="http://schema.org/Residence"
                                  className={`relative object-cover w-full h-full transition-all duration-500 select-none hover:scale-105 ${
                                    property?.is_sold ? "grayscale hover:grayscale-0" : "grayscale-0"
                                  }`}
                                  src={property?.thumbnail}
                                  loading={"lazy"}
                                />
                                {property?.is_sold && (
                                  <div className="absolute flex items-center justify-center p-2 overflow-hidden bg-white rounded-full pointer-events-none bottom-4 right-4 aspect-square text-stone-900 border-neutral-900">
                                    <p className="m-auto text-sm font-semibold">SOLD</p>
                                  </div>
                                )}
                              </div>
                              <div className="absolute top-0 left-0 right-0 p-4 pointer-events-none">
                                <span className="inline-block px-3 py-2 font-medium tracking-wide text-white bg-opacity-75 rounded bg-stone-900 drop-shadow-xl">
                                  {`${new Intl.NumberFormat("en-gb", {
                                    style: "currency",
                                    currency: "GBP",
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  }).format(
                                    property?.audience === "student"
                                      ? (property?.price * 12) / 52 / property?.beds
                                      : property?.price
                                  )} ${property?.audience === "student" ? "pppw" : "pcm"}`}
                                </span>
                              </div>
                              <div className="z-20 flex justify-between p-4 bg-[#e0c5b3] text-stone-900">
                                <div>
                                  <h2>
                                    {property?.address?.includes(",")
                                      ? property?.address?.split(",")?.[0]?.trim()
                                      : property?.address}
                                  </h2>
                                  <p className="font-bold">{property?.area}</p>
                                </div>
                                <div className="flex items-end text-center gap-x-4">
                                  {property?.beds > 0 ? (
                                    <div>
                                      <h2 className="text-lg">{property?.beds}</h2>
                                      <p className="-mt-1 text-sm">{property?.beds === 1 ? "bed" : "beds"}</p>
                                    </div>
                                  ) : (
                                    <div>
                                      <p className="-mt-1 text-sm">studio</p>
                                    </div>
                                  )}
                                  <div className="w-px h-full bg-stone-950"></div>
                                  <div>
                                    <h2 className="text-lg">{property?.baths}</h2>
                                    <p className="-mt-1 text-sm">{property?.baths === 1 ? "bath" : "baths"}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Link>
                        )
                      })}
                      <div className="absolute top-0 bottom-0 left-0 right-0 flex pointer-events-none">
                        <div className="w-full h-full px-4">
                          <div className="relative w-full h-full mx-auto transition-all duration-300 m:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                            <div className="absolute top-0 bottom-0 flex w-10 -left-3">
                              <div className="m-auto">
                                <button
                                  aria-label="Carousel"
                                  className={`block p-2 transition-all duration-300 bg-white border rounded-full pointer-events-auto drop-shadow ${
                                    carouselIndex === 0
                                      ? "cursor-not-allowed text-stone-400 border-stone-400"
                                      : "cursor-pointer text-stone-900 border-stone-900 "
                                  }`}
                                  onClick={() => {
                                    if (carouselIndex === 0) return

                                    setCarouselIndex(carouselIndex => Math.max(0, carouselIndex - 1))
                                    carouselRef.current.scrollTo({
                                      left: carouselRef.current.scrollLeft - carouselRef.current.clientWidth,
                                      behavior: "smooth",
                                    })
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="w-5 h-5"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M11.03 3.97a.75.75 0 010 1.06l-6.22 6.22H21a.75.75 0 010 1.5H4.81l6.22 6.22a.75.75 0 11-1.06 1.06l-7.5-7.5a.75.75 0 010-1.06l7.5-7.5a.75.75 0 011.06 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            <div className="absolute top-0 bottom-0 flex w-10 -right-3">
                              <div className="m-auto">
                                <button
                                  aria-label="Carousel"
                                  onClick={() => {
                                    if (carouselIndex === properties.length - 1) return

                                    setCarouselIndex(carouselIndex => carouselIndex + 1)
                                    carouselRef.current.scrollTo({
                                      left: carouselRef.current.scrollLeft + carouselRef.current.clientWidth,
                                      behavior: "smooth",
                                    })
                                  }}
                                  className={`block p-2 transition-all duration-300 bg-white border rounded-full pointer-events-auto drop-shadow ${
                                    carouselIndex === properties?.length - 1
                                      ? "cursor-not-allowed text-stone-400 border-stone-400"
                                      : "cursor-pointer text-stone-900 border-stone-900 "
                                  }`}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="w-5 h-5"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M12.97 3.97a.75.75 0 011.06 0l7.5 7.5a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 11-1.06-1.06l6.22-6.22H3a.75.75 0 010-1.5h16.19l-6.22-6.22a.75.75 0 010-1.06z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center w-full mt-6 gap-x-6">
                    {/* <Link href="/rent" prefetch={false}>
                      <button
                        aria-label="Properties For Sale"
                        className="px-4 py-3 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 hover:bg-cpink-50 hover:border-cpink-50 bg-cpink border-cpink"
                      >
                        Properties for sale
                      </button>
                    </Link> */}
                    <Link href="/rent" prefetch={false}>
                      <button
                        aria-label="Properties For Rent"
                        className="px-4 py-3 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 hover:bg-cpink-50 hover:border-cpink-50 bg-cpink border-cpink"
                      >
                        View all properties
                      </button>
                    </Link>
                  </div>
                </div>
                <section>
                  <div className="relative flex bg-white">
                    <div className="justify-center hidden w-full mx-auto max-h-64 sm:flex sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl">
                      <div className="absolute bottom-0 left-0 right-0 flex top-12 bg-cpink"></div>
                      <div className="z-10 items-center justify-center w-1/4 px-4 -mt-48 sm:-mt-0 sm:-mb-32 sm:w-2/5 md:-mb-48 lg:w-1/6 xl:w-2/7">
                        <img alt="Phone" className="w-full select-none" src="/images/phone-3.png" />
                        <img alt="Laptop" className="w-full select-none sm:hidden" src="/images/phone-3.png" />
                      </div>
                      <div className="z-10 flex w-3/5 pt-12 md:py-12 md:pt-[4.75rem]">
                        <div className="px-4 my-auto">
                          <h2 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                            Find out what your property is worth
                          </h2>
                          <Link href="/valuation?type=let" target="_blank">
                            <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                              Arrange a valuation
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white sm:hidden">
                    <div className="flex justify-center px-4 sm:hidden">
                      <img alt="Laptop" className="w-full max-w-sm select-none" src="/images/phone-3.png" />
                    </div>
                    <div className="relative px-4 -mt-96 bg-cpink">
                      <div className="flex py-12 ">
                        <div className="flex-1 min-w-0 sm:flex-2">
                          <h2 className="w-full font-serif text-4xl font-semibold text-stone-900 sm:text-left drop-shadow">
                            Find out what your property is worth
                          </h2>
                          <Link href="/valuation?type=let" target="_blank">
                            <button className="px-4 py-3 mt-6 font-medium transition-all duration-300 border-2 rounded select-none text-stone-900 bg-cpink-200 border-cpink-200 hover:bg-white hover:border-white">
                              Arrange a Valuation
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Footer />
                </section>
              </div>
            </div>
          </animated.div>
        </div>
        <div
          className={[
            "bg-transparent fixed z-30 top-0 bottom-0 left-0 right-0 transition-all duration-300 backdrop-blur-sm",
            isMenuOpen ? "opacity-100 visible cursor-pointer pointer-events-auto" : "opacity-0 pointer-events-none",
          ].join(" ")}
          onClick={() => {
            setIsMenuOpen(false)
            document?.body?.classList?.remove("overflow-hidden")
          }}
        />
        <div
          className={[
            "fixed top-0 bottom-0 left-0 right-0 bg-transparent transition-all duration-300 sm:hidden backdrop-b bg-stone-900",
            isMenuOpen ? "opacity-100 visible cursor-pointer" : "opacity-0 pointer-events-none",
          ].join(" ")}
          onClick={() => {
            setIsMenuOpen(false)
          }}
        />
        <div
          className={[
            "fixed bottom-0 left-0 right-0 flex flex-col m-4 overflow-hidden rounded sm:top-0 sm:bottom-auto shadow-stone-900",
            isMenuOpen ? "z-30" : "pointer-events-none",
            isMenuOpen && isMenuSearchOpen ? "h-full justify-end" : "",
          ].join(" ")}
          style={{ filter: `drop-shadow(0 0 25px rgb(0 0 0 / 0.375))` }}
        >
          <div
            className={[
              isMenuOpen ? "mt-8 mb-4" : "mb-0",
              isMenuOpen && isMenuSearchOpen
                ? "transition-all duration-300 ease-out max-h-full h-full overflow-y-scroll no-scrollbar"
                : "transition-all duration-300 ease-out max-h-[31.625rem] h-fit",
            ].join(" ")}
          >
            <div
              className={[
                "w-full bg-cpink sm:max-w-screen-sm md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl 2xl:max-w-screen-2xl mx-auto sm:hidden rounded overflow-scroll no-scrollbar",
                isMenuOpen
                  ? "transition-all translate-y-0 duration-300"
                  : "translate-y-full pointer-events-none transition-all ease-out duration-300 sm:hidden max-h-0",
                isMenuOpen && isMenuSearchOpen ? "max-h-full h-full" : "",
                isMenuOpen || isSearchOpen ? "border-b-0" : "",
              ].join(" ")}
            >
              <div>
                <div
                  className="sticky top-0 overflow-hidden bg-white border-b border-stone-900"
                  onClick={() => setIsMenuSearchOpen(true)}
                >
                  <SearchInput
                    setIsSearchOpen={setIsMenuSearchOpen}
                    isSearchOpen={isMenuSearchOpen}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                  />
                </div>
                <div
                  className={[
                    "flex flex-col text-lg leading-none text-stone-900 overflow-hidden no-scrollbar",
                    isMenuOpen && isMenuSearchOpen ? "hidden" : "",
                  ].join(" ")}
                >
                  {/* <Link
                    prefetch={false}
                    href="/rent"
                    className={["px-4 border-b py-5 border-stone-900 hover:bg-cpink-50 flex justify-between items-center"].join(
                      " "
                    )}
                  >
                    <p>BUY</p>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link> */}

                  <Link
                    prefetch={false}
                    href="/rent"
                    className={["px-4 border-b py-5 border-stone-900 hover:bg-cpink-50 flex justify-between items-center"].join(
                      " "
                    )}
                  >
                    <p>PROPERTIES</p>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                  {/* <Link
                    prefetch={false}
                    href="/valuation?type=sell"
                    className={["px-4 border-b py-5 border-stone-900 hover:bg-cpink-50 flex justify-between items-center"].join(
                      " "
                    )}
                  >
                    <p>SELL</p>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link> */}
                  <Link
                    prefetch={false}
                    href="/landlords"
                    className={["px-4 border-b py-5 border-stone-900 hover:bg-cpink-50 flex justify-between items-center"].join(
                      " "
                    )}
                  >
                    <p>LANDLORDS</p>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                  <Link
                    prefetch={false}
                    href="/contact"
                    className={["px-4 border-b py-5 border-stone-900 hover:bg-cpink-50 flex justify-between items-center"].join(
                      " "
                    )}
                  >
                    <p>CONTACT</p>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                  {/* <Link
                    prefetch={false}
                    href="tel:+4401509320320"
                    className={[
                      "px-4 border-b border-stone-900 py-5 bg-cpink-200 hover:bg-cpink-50 flex justify-between items-center",
                    ].join(" ")}
                  >
                    <p>SALES</p>
                    <p>01509 320 320</p>
                  </Link> */}
                  <Link
                    prefetch={false}
                    href="tel:+4401509320320"
                    className={[
                      "px-4 border-b border-stone-900 py-5 bg-cpink-200 hover:bg-cpink-50 flex justify-between items-center",
                    ].join(" ")}
                  >
                    <p>LETTINGS</p>
                    <p>01509 320 320</p>
                  </Link>
                </div>
                {isMenuSearchOpen && searchResults?.areas?.length !== 0 && (
                  <div className="px-4 py-3 text-sm text-white bg-teal-900">
                    {validator.isEmpty(searchQuery) ? "Popular Areas" : "Areas"}
                  </div>
                )}
                {isMenuSearchOpen && (
                  <div>
                    {searchResults?.areas
                      ?.filter(result => result?.type !== "for_sale")
                      ?.map((result, index) => (
                        <Link
                          className={["pointer-events-auto transition-all duration-300"].join(" ")}
                          href={`/${result.type === "for_sale" ? "buy" : "rent"}?area=${result.area}`}
                          key={index}
                          prefetch={false}
                          onClick={() => document?.body?.classList.remove("overflow-hidden")}
                        >
                          <div className="flex items-center justify-between p-4 bg-transparent border-b border-stone-900 text-stone-800 hover:bg-cpink-50">
                            <div>
                              <p className="text-sm text-stone-900">{result.type === "for_sale" ? "Buy in" : "Rent in"}</p>
                              <p className="font-medium leading-tight">{result.area}</p>
                            </div>
                            <p className="text-right">{`${result.count} ${result.count > 1 ? "properties" : "property"}`}</p>
                          </div>
                        </Link>
                      ))}
                  </div>
                )}
                {isMenuSearchOpen && searchResults?.properties?.length > 0 && (
                  <>
                    <div>
                      <div className="px-4 py-3 text-sm text-white bg-teal-900">Properties</div>
                    </div>
                    {searchResults?.properties
                      ?.filter(result => result?.type !== "for_sale")
                      ?.map((result, index) => (
                        <Link
                          className={["pointer-events-auto transition-all duration-300"].join(" ")}
                          href={`/${result.type === "for_sale" ? "buy" : "rent"}/${result.id}`}
                          key={index}
                          prefetch={false}
                        >
                          <div className="flex items-center justify-between p-4 bg-transparent border-b border-stone-900 text-stone-800 hover:bg-cpink-50">
                            <div>
                              <p className="text-sm text-stone-900">{result.area}</p>
                              <p className="font-medium leading-tight">{result.address}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-md">
                                {new Intl.NumberFormat("en-GB", {
                                  style: "currency",
                                  currency: "GBP",
                                  maximumFractionDigits: 0,
                                }).format(result?.price)}
                              </p>
                              <p className="text-sm text-stone-900">
                                {result?.beds} {result?.beds === 1 ? "bed" : "beds"} • {result.baths}{" "}
                                {result?.baths === 1 ? "bath" : "baths"}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))}
                  </>
                )}
              </div>
            </div>
          </div>
          {!isSearchOpen && (
            <div
              className={[
                `flex items-center relative mx-auto sm:max-w-[calc(640px+128px)] md:max-w-[calc(768px+128px)] lg:max-w-[calc(1024px+128px)] xl:max-w-[calc(1280px+128px)] justify-between p-2 rounded 2xl:max-w-[calc(1536px+128px)] w-full transition-all duration-300 bg-teal-900`,
                isMenuOpen ? "sm:shadow-sm" : "mt-auto",
                isSearchOpen ? "hidden sm:flex" : "",
              ].join(" ")}
            >
              <Link aria-label="Home" href="/" className="ml-2 -mb-1 pointer-events-auto" prefetch={false}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 230 52" className="h-6">
                  <path
                    fill="#fff"
                    d="M26.235 34.235V23.529H13.176v10.706l3.883 3.53v1.294H0v-1.295l3.882-3.529V8.588L0 5.058V3.766h17.059V5.06l-3.883 3.53v11.176h13.06V8.588l-3.883-3.53V3.766H39.41V5.06l-3.882 3.53v25.646l3.882 3.53v1.294H22.235v-1.295l4-3.529zM44.823 18.47l-3.176-2.352v-1.294L53.41 12v15.53c0 1.882.353 3.293.941 4.235.589.823 1.647 1.294 3.059 1.294 1.176 0 2.588-.353 4.118-.941V18.47l-3.177-2.353v-1.294L70.117 12v22.117l4.235 1.648v1.294l-9.294 2.706-3.53-4.706c-.94 1.411-2 2.47-3.411 3.294-1.412.823-2.941 1.176-4.588 1.176-2.706 0-4.824-.823-6.353-2.47-1.647-1.647-2.47-3.883-2.47-6.706V18.47h.117zm52.235 16.706V24c0-3.647-1.294-5.53-3.882-5.53-1.647 0-3.177.471-4.706 1.412v15.294l2.942 2.588v1.295H76.588v-1.294l3.176-2.589v-16.47l-3.411-2.47V14.94L86 11.882l1.647 4.824c1.411-1.53 2.941-2.706 4.47-3.53 1.53-.823 3.177-1.176 5.06-1.176 2.587 0 4.705.823 6.234 2.47 1.529 1.648 2.353 3.883 2.353 6.706v14.118L108.94 38v1.294H94.117V38l2.941-2.824zm26.118-29.411v6.588h7.058v3.764h-7.058v14c0 1.883.353 3.177.941 3.883s1.647 1.059 2.941 1.059c1.412 0 2.824-.118 3.882-.471v1.294c-.47.47-1.176 1.059-2.235 1.647-.941.588-2.117 1.177-3.412 1.53-1.293.352-2.588.588-3.764.588-2.236 0-4-.588-5.294-2-1.294-1.294-1.883-3.177-1.883-5.53v-16h-4.118v-2.47l4.118-.588V7.765l8.824-2zm13.175 29.411V6.471l-3.176-2.353V2.824L144.94 0v35.176l3.176 2.588v1.294h-14.941v-1.294l3.176-2.588zm39.765-9.882h-15.647c.118 2 .471 3.647.942 4.941.471 1.412 1.412 2.47 2.588 3.294 1.177.824 2.941 1.294 5.177 1.294 1.764 0 3.764-.353 5.882-1.176l.823 1.647c-1.176 1.294-2.706 2.235-4.588 3.059-1.882.823-3.882 1.176-6.235 1.176-4.353 0-7.765-1.176-10.236-3.53-2.47-2.352-3.646-5.646-3.646-9.882 0-1.882.352-3.647.941-5.411a11.568 11.568 0 012.823-4.588c1.177-1.294 2.706-2.353 4.471-3.177 1.765-.823 3.647-1.176 5.647-1.176 1.294 0 2.588.117 3.882.47 1.295.353 2.471.941 3.647 1.883 1.059.823 2 2 2.706 3.411.706 1.412 1.059 3.06 1.059 5.059.117.941 0 1.765-.236 2.706zM164.822 16c-.588 0-1.176.235-1.882.588-.706.47-1.176 1.177-1.647 2.235-.47 1.06-.706 2.353-.824 3.765l8.235-1.059c-.116-1.882-.588-3.294-1.176-4.235-.588-.823-1.529-1.294-2.706-1.294zm17.177 36l-2.352-7.647c.235 0 1.059-.118 2.588-.236 1.529-.117 2.823-.352 3.764-.705 1.059-.353 2.001-.824 2.824-1.647.824-.706 1.647-1.648 2.235-2.706l-10.47-22.706-3.176-2.588V12.47h15.647v1.294l-3.177 2.588 5.294 12.941 4.823-12.941-3.176-2.588V12.47h10.823v1.294l-3.411 2.588-9.412 22.823c-1.53 3.648-2.706 6.236-3.647 7.647-.941 1.412-2.117 2.589-3.53 3.412-1.411.824-3.294 1.412-5.647 1.765zm47.882-21.059c0 2.824-1.059 4.941-3.176 6.47-2.118 1.412-5.059 2.236-8.824 2.236-1.294 0-2.588-.118-3.882-.236-1.294-.117-2.941-.588-4.706-1.058v-8.824h1.295l4.117 6.118c.824.235 1.646.47 2.235.47 3.059 0 4.706-.94 4.706-2.705 0-.471-.236-1.06-.706-1.647-.47-.589-1.765-1.412-3.882-2.353-2.236-.941-3.765-1.883-4.706-2.706-.941-.824-1.648-1.647-2.118-2.706-.471-1.059-.706-2.235-.706-3.53 0-2.47.942-4.588 2.941-6.117 2.001-1.53 4.707-2.353 8.236-2.353 2.588 0 4.941.47 7.293 1.53v7.764h-1.293l-3.882-5.53c-2.001 0-3.294.236-3.883.707-.706.47-.941 1.176-.941 2 0 .352.117.705.235 1.058.235.353.471.706.941 1.06.471.352 1.883 1.058 4.353 2.235 2.471 1.176 4.236 2.352 5.059 3.646.824 1.295 1.294 2.706 1.294 4.471z"
                  ></path>
                </svg>
              </Link>
              <button
                aria-label="search"
                className="rounded pointer-events-auto hover:bg-teal-800 sm:hidden"
                onClick={() => {
                  setIsMenuSearchOpen(false)
                  setIsMenuOpen(isMenuOpen => !isMenuOpen)
                }}
              >
                <Hamburger toggled={isMenuOpen} color="#fff" direction="sm" size={18} />
              </button>
              <div className="hidden text-white sm:flex gap-x-4">
                {/* <Link
                  href="/rent"
                  className="hidden px-3 py-2 text-center transition-all duration-300 rounded pointer-events-auto hover:bg-teal-800 sm:block"
                >
                  Buy
                </Link> */}
                <Link
                  href="/rent"
                  className="hidden px-3 py-2 text-center transition-all duration-300 rounded pointer-events-auto hover:bg-teal-800 sm:block"
                >
                  Properties
                </Link>
                <Link
                  href="/landlords"
                  className="hidden px-3 py-2 text-center transition-all duration-300 rounded pointer-events-auto hover:bg-teal-800 sm:block"
                >
                  Landlords
                </Link>
                <Link
                  href="/valuation?type=let"
                  className="hidden px-3 py-2 text-center transition-all duration-300 rounded pointer-events-auto hover:bg-teal-800 sm:block"
                >
                  Sell
                </Link>
                <Link
                  href="/contact"
                  className="hidden px-3 py-2 text-center transition-all duration-300 rounded pointer-events-auto hover:bg-teal-800 sm:block"
                >
                  Contact
                </Link>
              </div>
            </div>
          )}
        </div>
        {showVideoOverlay && (
          <div className={[`fixed top-0 bottom-0 left-0 right-0 flex flex-col w-screen h-screen bg-stone-900`].join(" ")}>
            <div className="flex justify-between w-full p-4 sm:px-4 bg-cpink text-stone-900 border-stone-800">
              <p className="text-md">Let with Huntleys</p>
              <button
                className="transition-all duration-300 hover:text-white"
                onClick={() => {
                  setIsVideoOpen(false)
                  document.body.classList.remove("overflow-hidden")
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
            <div className="w-full h-full">
              <YouTube
                videoId="Ky_TDWvKBmQ"
                opts={{
                  height: "100%",
                  width: "100%",
                  playerVars: {
                    autoplay: 1,
                    color: "white",
                  },
                }}
                className="w-full h-full overflow-hidden"
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default HomePage

